import dayjs from 'dayjs';
import { formatDateString } from 'lib/functions/utils';

import { Entry as BookEntryModalEntry } from '../BookEntries/components/Modal';
import { BankReconciliationEntryReference } from './components/table';
export const formatDate = (value?: string) => {
  if (!value) return '-';
  const formattedDate = formatDateString(value, 'dd/mm/yyyy');
  return formattedDate === 'Fecha inválida' ? '-' : formattedDate;
};

export const formatAmount = (value?: number) => {
  if (typeof value !== 'number') return '-';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(value);
};

export const normalizeEditDate = (
  value: unknown,
  emptyEditDate: string,
): string => {
  if (typeof value !== 'string' || value.length === 0) return emptyEditDate;
  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : emptyEditDate;
};

export const normalizeDateOnly = (value: string): string => {
  const normalizedDate = formatDateString(value, 'yyyy-mm-dd');
  return normalizedDate === 'Fecha inválida' ? value : normalizedDate;
};

import {
  BankReconciliationAccountingLine,
  BankReconciliationAccountingLinesResponse,
  BankReconciliationGroupedBank,
  BankReconciliationLineEdit,
} from 'lib/api/accounting/types';

export type NormalizedBankReconciliationResponse = {
  items: BankReconciliationAccountingLine[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
};

export const getLineRowKey = (
  line: BankReconciliationAccountingLine,
  index: number,
) =>
  String(
    line.lineKey ??
      line.id ??
      line.accountingLineId ??
      line.movementKey ??
      line.sourceKey ??
      `line-${index}`,
  );

export const toLineEdit = (
  line: BankReconciliationAccountingLine,
  index: number,
  normalizeEditDate: (value: unknown) => string,
): BankReconciliationLineEdit => ({
  rowKey: getLineRowKey(line, index),
  lineKey: typeof line.lineKey === 'string' ? line.lineKey : undefined,
  lineId: line.id,
  accountingLineId: line.accountingLineId,
  movementKey:
    typeof line.movementKey === 'string' ? line.movementKey : undefined,
  isReconciled: Boolean(line.isConciliated ?? line.isReconciled),
  observation:
    typeof line.observation === 'string' && line.observation.trim().length > 0
      ? line.observation
      : typeof line.description === 'string'
        ? line.description
        : '',
  reconciledAt:
    normalizeEditDate(line.conciliationDate ?? line.reconciledAt) || null,
});

export const areLineEditsEqual = (
  left?: BankReconciliationLineEdit,
  right?: BankReconciliationLineEdit,
) => {
  if (!left || !right) return false;
  return (
    left.isReconciled === right.isReconciled &&
    left.observation === right.observation &&
    left.reconciledAt === right.reconciledAt
  );
};

export const normalizeResponse = (
  response: BankReconciliationAccountingLinesResponse | undefined,
  fallbackPage: number,
  fallbackLimit: number,
): NormalizedBankReconciliationResponse => {
  const groupedLines = (response?.groupedByBank ?? []).flatMap(
    (group: BankReconciliationGroupedBank) =>
      (group.lines ?? []).map((line: BankReconciliationAccountingLine) => ({
        ...line,
        bankId: group.bankId,
        internalKey: group.internalKey,
        account: line.account ?? group.account,
        accountName: line.accountName ?? group.accountName,
      })),
  );

  const items =
    groupedLines.length > 0
      ? groupedLines
      : (response?.items ??
        response?.accountingLines ??
        response?.data ??
        response?.results ??
        []);

  const totalItems =
    response?.pagination?.total ??
    response?.totalLines ??
    response?.total ??
    response?.totalItems ??
    (Array.isArray(items) ? items.length : 0);

  const currentPage =
    response?.pagination?.page ??
    response?.page ??
    response?.currentPage ??
    fallbackPage;
  const pageSize =
    response?.pagination?.pageSize ??
    response?.limit ??
    response?.pageSize ??
    fallbackLimit;
  const totalPages =
    response?.pagination?.totalPages ??
    response?.totalPages ??
    (pageSize > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1);

  return {
    items: Array.isArray(items) ? items : [],
    totalItems,
    currentPage,
    pageSize,
    totalPages,
  };
};

type TransactionalEntryLine = {
  id?: string | number | null;
  movementKey?: string;
  account?: string;
  accountName?: string;
  documentId?: string | null;
  debit?: number;
  credit?: number;
  description?: string;
  businessIdentifier?: string | null;
};

type TransactionalEntry = {
  category?: string;
  businessIdentifier?: string;
  businessName?: string;
  date?: string;
  sourceKey?: string;
  groupedLines?: TransactionalEntryLine[];
};

export type TransactionalDay = {
  date?: string;
  groupedLinesByCategoryAndBusiness?: TransactionalEntry[];
};

const toMoneyComparable = (value?: number) =>
  typeof value === 'number' ? Number(value.toFixed(0)) : undefined;

export const findEntryFromTransactionalDay = (
  entries: TransactionalEntry[],
  reference: BankReconciliationEntryReference,
) => {
  const movementKey = reference.movementKey?.trim();
  if (movementKey) {
    const byMovementKey = entries.find((entry) =>
      (entry.groupedLines ?? []).some(
        (line) => (line.movementKey ?? '').trim() === movementKey,
      ),
    );
    if (byMovementKey) return byMovementKey;
  }

  if (
    reference.accountingEntryNumber !== undefined &&
    reference.accountingEntryNumber !== null
  ) {
    const targetEntryId = String(reference.accountingEntryNumber);
    const byEntryId = entries.find((entry) =>
      (entry.groupedLines ?? []).some(
        (line) => String(line.id ?? '') === targetEntryId,
      ),
    );
    if (byEntryId) return byEntryId;
  }

  const targetDebit = toMoneyComparable(reference.debit);
  const targetCredit = toMoneyComparable(reference.credit);
  const targetDescription = (reference.description ?? '').trim().toLowerCase();

  return entries.find((entry) =>
    (entry.groupedLines ?? []).some((line) => {
      const lineDebit = toMoneyComparable(line.debit);
      const lineCredit = toMoneyComparable(line.credit);
      const lineDescription = (line.description ?? '').trim().toLowerCase();
      return (
        (targetDebit === undefined || lineDebit === targetDebit) &&
        (targetCredit === undefined || lineCredit === targetCredit) &&
        (targetDescription.length === 0 ||
          lineDescription === targetDescription)
      );
    }),
  );
};

export const mapTransactionalEntryToModalEntry = (
  entry: TransactionalEntry,
  date: string,
): BookEntryModalEntry => {
  const mappedLines = (entry.groupedLines ?? []).map((line) => ({
    entryId: line.id === null || line.id === undefined ? '' : String(line.id),
    account: line.account ?? '',
    accountName: line.accountName ?? '',
    description: line.description ?? '',
    businessIdentifier: line.businessIdentifier ?? '',
    auxiliaryBusinessIdentifier: line.businessIdentifier ?? '',
    documentId: line.documentId ?? '',
    debit: typeof line.debit === 'number' ? line.debit : 0,
    credit: typeof line.credit === 'number' ? line.credit : 0,
  }));
  return {
    businessIdentifier: entry.businessIdentifier ?? '',
    businessName: entry.businessName ?? '',
    category: entry.category ?? '',
    date: entry.date ?? date,
    sourceKey: entry.sourceKey,
    groupedLines: mappedLines,
  };
};
